import * as Sentry from '@sentry/react';

/**
 * Validation issue structure for Sentry logging.
 * Mirrors ValidationIssue from features layer to avoid circular dependencies.
 */
export interface ValidationIssueForLogging {
  path: unknown[];
  pathKey: string;
  message: string;
  severity: 'ERROR' | 'WARNING';
  key: string;
}

/**
 * Logs schema validation errors to Sentry for monitoring.
 * Only logs ERROR-level issues with high severity.
 *
 * @param endpoint - API endpoint name (e.g., 'activeCases', 'hubsResourceReport')
 * @param errorIssues - Array of ERROR severity validation issues
 * @param totalItems - Total number of items in response
 * @param filteredCount - Number of items filtered due to errors
 */
export const logValidationErrorsToSentry = (
  endpoint: string,
  errorIssues: ValidationIssueForLogging[],
  totalItems: number,
  filteredCount: number,
): void => {
  if (errorIssues.length === 0) {
    return;
  }

  // Group issues by key for better readability
  const issuesByKey = errorIssues.reduce(
    (acc, issue) => {
      if (!acc[issue.key]) {
        acc[issue.key] = [];
      }
      acc[issue.key].push({
        path: issue.pathKey,
        message: issue.message,
      });
      return acc;
    },
    {} as Record<string, Array<{ path: string; message: string }>>,
  );

  // Create a structured message
  const message = `Schema validation errors in ${endpoint}: ${filteredCount} of ${totalItems} items filtered`;

  // Log to Sentry with ERROR level
  Sentry.captureMessage(message, {
    level: 'error',
    tags: {
      validationError: true,
      endpoint,
      filteredCount: filteredCount.toString(),
    },
    extra: {
      endpoint,
      totalItems,
      filteredCount,
      errorCount: errorIssues.length,
      issuesByKey,
      allIssues: errorIssues.map(issue => ({
        key: issue.key,
        path: issue.pathKey,
        message: issue.message,
        severity: issue.severity,
      })),
    },
    fingerprint: ['validation-error', endpoint],
  });
};
